import lodashGet from "lodash/get";
import lodashMemoize from "lodash/memoize";
import memoizeOne from "memoize-one";
import { IStore } from "reducers";
import {
    normalizeCollectionAllocationDimension,
    AllocationDimensionFields,
    AllocationDimensionKind,
    AllocationDimensionIndex,
    getAllocationDimensionByIndex,
} from "helpers/allocation-dimension";
import { ALLOCATION_DIMENSION_ID_PREFIX, CollectionKeys } from "constants/product-collection";
import { getCollection } from ".";
import { getSnapshot, getHEAD } from "../mutate";
import { nameExtRemover } from "containers/folders/utils/space-item";
import { ItemKind } from "containers/folders/types/base";

export const getAllocationDimensionById = (state: IStore, allocationDimensionId: string, poi?: string) => {
    const allocationDimensionObj = getSnapshot(state, allocationDimensionId, poi);
    const allocationDimensionRef = getHEAD(state, allocationDimensionId, poi);
    if (allocationDimensionRef && allocationDimensionObj) {
        return normalizeCollectionAllocationDimension(allocationDimensionRef, allocationDimensionObj.toJS());
    }

    return undefined;
};

// This returns allocation dimensions as ordered in collection
const getAllocationDimensionsRaw = (state: IStore, collectionId: string, poi?: string) => {
    const allocationDimensions = [];

    const { mutate } = state;
    const collectionRoot = mutate.snapshots.get(collectionId);
    if (!collectionRoot) {
        return allocationDimensions;
    }

    const allocationDimensionOrders = collectionRoot.getIn(["item_allocation_dimension_order", "list_value"]);
    if (!allocationDimensionOrders) {
        return allocationDimensions;
    }

    allocationDimensionOrders.forEach((unprefixedAllocationDimensionId) => {
        // Collection allocation dimension id is prefixed
        const allocationDimensionId = `${ALLOCATION_DIMENSION_ID_PREFIX}${unprefixedAllocationDimensionId}`;

        const allocationDimension = getAllocationDimensionById(state, allocationDimensionId, poi);
        if (allocationDimension) {
            allocationDimensions.push(allocationDimension);
        }
    });

    return allocationDimensions;
};

// This returns allocation dimensions values Id ordered
const getAllocationDimensionsValuesOrder = (
    state: IStore,
    collectionId: string,
    allocationDimensionIndex: AllocationDimensionIndex
) => {
    const { mutate } = state;
    const collectionRoot = mutate.snapshots.get(collectionId);
    if (!collectionRoot) {
        return [];
    }

    const allocationDimensionsValuesOrderList = collectionRoot.toJS().orderForAllocationDimensionsValues;

    if (allocationDimensionsValuesOrderList?.length > 1) {
        return allocationDimensionsValuesOrderList[allocationDimensionIndex];
    }

    return allocationDimensionsValuesOrderList[allocationDimensionIndex - 1] || [];
};

// Build cache key off collection's allocation dimensions id and revisions
const allocationDimensionsResolverFn = (...params: Parameters<typeof getAllocationDimensionsRaw>) => {
    const [state, collectionId, poi] = params;
    const collection = getCollection(...params);
    let cacheKey;

    if (collection && state.collection?.driveItem) {
        const collectionName = nameExtRemover(state.collection?.driveItem?.name, ItemKind.CollectionDocument);
        cacheKey = [collectionId, collectionName].join("_");
        const allocationDimensionOrders = collection.getIn(["item_allocation_dimension_order", "list_value"]);

        allocationDimensionOrders?.forEach((unprefixedAllocationDimensionId) => {
            // Collection allocation dimension id is prefixed
            const allocationDimensionId = `${ALLOCATION_DIMENSION_ID_PREFIX}${unprefixedAllocationDimensionId}`;

            const allocationDimensionHEAD = getHEAD(state, allocationDimensionId, poi);
            if (allocationDimensionHEAD) {
                cacheKey = `${cacheKey}_${allocationDimensionHEAD.object_id}_${allocationDimensionHEAD.revision}`;
            }
        });
    }

    return cacheKey || state;
};

export const getAllocationDimensions = lodashMemoize(getAllocationDimensionsRaw, allocationDimensionsResolverFn);

export const getAllocationDimensionsBySubtypeRaw = memoizeOne((allocationDimensions, subtype) =>
    allocationDimensions.filter((allocationDimension) => allocationDimension.subtype === subtype)
);

export const getAllocationDimensionsBySubtype = (
    state: IStore,
    collectionId: string,
    subtype: AllocationDimensionKind,
    poi?: string
) => {
    return getAllocationDimensionsBySubtypeRaw(getAllocationDimensions(state, collectionId, poi), subtype);
};

export const getAllocationDimensionValues = (
    state: IStore,
    collectionId: string,
    allocationDimensionIndex: AllocationDimensionIndex,
    poi?: string
) => {
    const allocationDimensions = getAllocationDimensionsBySubtype(
        state,
        collectionId,
        AllocationDimensionKind.Generic,
        poi
    );
    const allocationDimension = getAllocationDimensionByIndex(allocationDimensions, allocationDimensionIndex);
    return lodashGet(allocationDimension || {}, AllocationDimensionFields.dimensionValues);
};

export const getRollupAllocationDimensionValues = (
    state: IStore,
    collectionId: string,
    allocationDimensionIndex: AllocationDimensionIndex,
    poi?: string
) => {
    const allocationDimensions = getAllocationDimensionsBySubtype(
        state,
        collectionId,
        AllocationDimensionKind.Generic,
        poi
    );
    const allocationDimension = getAllocationDimensionByIndex(allocationDimensions, allocationDimensionIndex);

    if (allocationDimension?.dimension_values) {
        // get the list Of reference to sort the allocation dimension values
        const allocationDimensionsValuesOrder = getAllocationDimensionsValuesOrder(
            state,
            collectionId,
            allocationDimensionIndex
        );

        if (allocationDimensionsValuesOrder) {
            allocationDimension.dimension_values = [...allocationDimension.dimension_values].sort(
                (a, b) => allocationDimensionsValuesOrder.indexOf(a.id) - allocationDimensionsValuesOrder.indexOf(b.id)
            );
        }
    }

    return lodashGet(allocationDimension || {}, AllocationDimensionFields.dimensionValues);
};

export const getRollupDoorsDimensionValues = (state: IStore, collectionId: string, poi?: string) => {
    return getAllocationDimensionsBySubtype(state, collectionId, AllocationDimensionKind.Generic, poi)
        .filter((allocationDimension) => allocationDimension.name === "Doors")
        .map((allocationDimension) => {
            return {
                id: allocationDimension.id,
                doors: lodashGet(allocationDimension || {}, AllocationDimensionFields.dimensionValues),
            };
        });
};

export const getAllocationDimensionAttributes = (
    state: IStore,
    collectionId: string,
    index: AllocationDimensionIndex,
    poi?: string
) => {
    const allocationDimensions = getAllocationDimensionsBySubtype(
        state,
        collectionId,
        AllocationDimensionKind.Generic,
        poi
    );
    const allocationDimension = getAllocationDimensionByIndex(allocationDimensions, index);
    if (!allocationDimension) {
        return undefined;
    }
    return lodashGet(allocationDimension, AllocationDimensionFields.attributes.concat(["keys"]));
};

const getMetricsFromSchemaRaw = (state: IStore, collectionId: string) => {
    const collectionRoot = getSnapshot(state, collectionId);
    return collectionRoot?.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.DOOR_GROUP_METRICS)?.toJS() ?? [];
};

export const getMetricsFromSchema = memoizeOne(
    (state: IStore, collectionId: string) => {
        return getMetricsFromSchemaRaw(state, collectionId);
    },
    (newArgs: Parameters<typeof getMetricsFromSchemaRaw>, oldArgs: Parameters<typeof getMetricsFromSchemaRaw>) => {
        const newCollectionHEAD = getHEAD(...newArgs);
        const oldCollectionHEAD = getHEAD(...oldArgs);

        return newCollectionHEAD.revision === oldCollectionHEAD.revision;
    }
);
